import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from 'recharts'
import { api } from '../utils/api'

const STATUS_COLORS: Record<string, string> = { OPEN: '#3b82f6', ASSIGNED: '#6366f1', IN_PROGRESS: '#8b5cf6', PENDING: '#f59e0b', RESOLVED: '#10b981', CLOSED: '#6b7280', CANCELLED: '#f87171' }
const PRIORITY_COLORS: Record<string, string> = { CRITICAL: '#ef4444', HIGH: '#f97316', MEDIUM: '#eab308', LOW: '#6b7280' }

// Standalone print view (no app layout) — opened in a new tab; auto-triggers the
// browser print dialog so the user can "Save as PDF". Same pattern as
// ZabbixReportPrintPage.

interface Summary {
  periodLabel?: string; from?: string; till?: string
  totals: { created: number; resolved: number; open: number; carriedOver: number; overdueOpen: number }
  byStatus: { key: string; label: string; count: number }[]
  byPriority: { key: string; label: string; count: number }[]
  trend?: { bucket: string; created: number; resolved: number }[]
  sla: {
    withSla: number; met: number; breached: number; compliancePercent: number | null
    byPriority: { priority: string; label: string; total: number; met: number; breached: number; pct: number | null }[]
  }
  resolution: { countResolved: number; avgHours: number | null; medianHours: number | null }
  categories: { name: string; count: number; breached: number; avgResolutionHours: number | null }[]
  topOverdue: {
    ticketNumber: number; title: string; priorityLabel: string; statusLabel: string
    dueDate: string; assigneeName: string | null
  }[]
}
interface Report {
  title: string; period: string; trigger: string; provider?: string | null
  status: string; error?: string | null; narrative?: string | null
  createdAt: string; createdByName?: string | null
  summary?: Summary | null
}

const hrs = (n: number | null | undefined) => (n == null ? '—' : n >= 24 ? `${(n / 24).toFixed(1)} วัน` : `${n} ชม.`)
const dt = (s?: string | null) => (s ? new Date(s).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' }) : '—')

function Markdown({ text }: { text: string }) {
  const lines = text.split('\n')
  const out: JSX.Element[] = []
  let list: string[] = []
  const inline = (s: string) =>
    s.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((p, i) => {
      if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>
      if (p.startsWith('`') && p.endsWith('`')) return <code key={i}>{p.slice(1, -1)}</code>
      return <span key={i}>{p}</span>
    })
  const flush = () => {
    if (list.length) { out.push(<ul key={`u${out.length}`}>{list.map((li, i) => <li key={i}>{inline(li)}</li>)}</ul>); list = [] }
  }
  lines.forEach((raw, idx) => {
    const line = raw.replace(/\s+$/, '')
    if (/^#{1,6}\s/.test(line)) { flush(); const lvl = line.match(/^#+/)![0].length; const c = line.replace(/^#+\s/, '')
      out.push(lvl <= 2 ? <h2 key={idx}>{inline(c)}</h2> : <h3 key={idx}>{inline(c)}</h3>) }
    else if (/^\s*[-*]\s+/.test(line)) list.push(line.replace(/^\s*[-*]\s+/, ''))
    else if (/^>\s?/.test(line)) { flush(); out.push(<blockquote key={idx}>{inline(line.replace(/^>\s?/, ''))}</blockquote>) }
    else if (line.trim() === '') flush()
    else { flush(); out.push(<p key={idx}>{inline(line)}</p>) }
  })
  flush()
  return <>{out}</>
}

export default function ServiceDeskReportPrintPage() {
  const { id } = useParams()
  const [report, setReport] = useState<Report | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    api.get(`/servicedesk/reports/${id}`)
      .then(r => setReport(r.data))
      .catch(e => setErr(e.response?.data?.message || 'โหลดรายงานไม่ได้'))
  }, [id])

  useEffect(() => {
    if (report) { const t = setTimeout(() => window.print(), 400); return () => clearTimeout(t) }
  }, [report])

  if (err) return <div style={{ padding: 40, fontFamily: 'sans-serif' }}>{err}</div>
  if (!report) return <div style={{ padding: 40, fontFamily: 'sans-serif' }}>กำลังโหลด…</div>

  const s = report.summary

  return (
    <div className="sd-print">
      <style>{`
        .sd-print { max-width: 820px; margin: 0 auto; padding: 32px 28px; color: #1a1a1a;
          font-family: 'Sarabun','TH Sarabun New','Segoe UI',sans-serif; font-size: 13px; line-height: 1.6; }
        .sd-print h1 { font-size: 20px; margin: 0 0 4px; }
        .sd-print h2 { font-size: 15px; margin: 20px 0 6px; border-bottom: 1px solid #ddd; padding-bottom: 3px; }
        .sd-print h3 { font-size: 13.5px; margin: 14px 0 4px; }
        .sd-print .meta { color: #555; font-size: 12px; margin-bottom: 4px; }
        .sd-print table { width: 100%; border-collapse: collapse; margin: 6px 0 14px; font-size: 12px; }
        .sd-print th, .sd-print td { border: 1px solid #d5d5d5; padding: 4px 7px; text-align: right; }
        .sd-print th:first-child, .sd-print td:first-child { text-align: left; }
        .sd-print th { background: #f3f3f3; }
        .sd-print ul { margin: 4px 0 10px; padding-left: 20px; }
        .sd-print blockquote { margin: 6px 0; padding: 4px 10px; background: #fff8e1; border-left: 3px solid #ffca28; }
        .sd-print code { background: #f0f0f0; padding: 0 3px; border-radius: 3px; font-size: .92em; }
        .sd-print .pill { display: inline-block; border: 1px solid #ccc; border-radius: 10px; padding: 1px 8px; margin: 0 4px 4px 0; font-size: 11px; }
        .sd-print .charts { display: flex; flex-wrap: wrap; gap: 12px; margin: 8px 0 16px; }
        .sd-print .chart-box { border: 1px solid #e5e5e5; border-radius: 8px; padding: 8px 10px; }
        .sd-print .chart-title { font-size: 12px; font-weight: 700; margin-bottom: 4px; }
        .sd-print .recharts-surface, .sd-print svg { overflow: visible; }
        @media print {
          .sd-print { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .sd-print .charts { page-break-inside: avoid; }
          @page { margin: 14mm; }
        }
      `}</style>

      <h1>{report.title}</h1>
      <div className="meta">
        ช่วงข้อมูล: {s?.periodLabel ?? report.period}
        {s?.from && <> ({new Date(s.from).toLocaleString('th-TH')} – {new Date(s.till!).toLocaleString('th-TH')})</>}
      </div>
      <div className="meta">
        สร้างเมื่อ: {new Date(report.createdAt).toLocaleString('th-TH', { dateStyle: 'long', timeStyle: 'short' })}
        {'  ·  '}ที่มา: {report.createdByName || 'ผู้ใช้'}
        {report.provider && <>{'  ·  '}AI: {report.provider}</>}
      </div>

      {report.status === 'FAILED' && (
        <p style={{ color: '#b00' }}><strong>สถานะ: ล้มเหลว</strong> — {report.error}</p>
      )}

      {report.narrative && (
        <>
          <h2>บทวิเคราะห์จาก AI</h2>
          <Markdown text={report.narrative} />
        </>
      )}

      {s?.totals && (
        <>
          <h2>ข้อมูลสรุป</h2>
          <div className="meta">
            สร้างใหม่: {s.totals.created} · แก้ไขได้: {s.totals.resolved} · ค้างอยู่: {s.totals.open} ·
            ยกมาจากช่วงก่อน: {s.totals.carriedOver} · เกิน SLA (ค้าง): {s.totals.overdueOpen}
          </div>
          <div style={{ margin: '4px 0 10px' }}>
            {s.byStatus.map(x => <span className="pill" key={x.key}>{x.label}: {x.count}</span>)}
          </div>

          <div className="charts">
            {s.trend && s.trend.length > 0 && (
              <div className="chart-box">
                <div className="chart-title">สร้าง vs แก้ไขได้</div>
                <BarChart width={760} height={240} data={s.trend} margin={{ top: 5, right: 10, left: -18, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="created" name="สร้าง" fill="#3b82f6" isAnimationActive={false} />
                  <Bar dataKey="resolved" name="แก้ไขได้" fill="#10b981" isAnimationActive={false} />
                </BarChart>
              </div>
            )}
            {s.byStatus.length > 0 && (
              <div className="chart-box">
                <div className="chart-title">ตามสถานะ</div>
                <BarChart width={360} height={230} data={s.byStatus} margin={{ top: 5, right: 10, left: -18, bottom: 5 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={0} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="จำนวน" isAnimationActive={false}>
                    {s.byStatus.map((x, i) => <Cell key={i} fill={STATUS_COLORS[x.key] || '#3b82f6'} />)}
                  </Bar>
                </BarChart>
              </div>
            )}
            {s.byPriority.length > 0 && (
              <div className="chart-box">
                <div className="chart-title">ตาม Priority</div>
                <PieChart width={340} height={230}>
                  <Pie data={s.byPriority} dataKey="count" nameKey="label" cx="45%" cy="50%" outerRadius={75}
                    isAnimationActive={false} label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                    {s.byPriority.map((x, i) => <Cell key={i} fill={PRIORITY_COLORS[x.key] || '#6b7280'} />)}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </div>
            )}
          </div>

          <h3>SLA Compliance</h3>
          <div className="meta">
            Ticket ที่มี SLA: {s.sla.withSla} · ผ่าน: {s.sla.met} · เกิน: {s.sla.breached} ·
            Compliance: {s.sla.compliancePercent != null ? `${s.sla.compliancePercent}%` : '—'}
          </div>
          {s.sla.byPriority.length > 0 && (
            <table>
              <thead><tr><th>Priority</th><th>ทั้งหมด</th><th>ผ่าน</th><th>เกิน</th><th>Compliance</th></tr></thead>
              <tbody>
                {s.sla.byPriority.map(r => (
                  <tr key={r.priority}>
                    <td>{r.label}</td><td>{r.total}</td><td>{r.met}</td><td>{r.breached}</td>
                    <td>{r.pct != null ? `${r.pct}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="meta">
            เวลาแก้ไข: เฉลี่ย {hrs(s.resolution.avgHours)} · มัธยฐาน {hrs(s.resolution.medianHours)} (จาก {s.resolution.countResolved} ticket)
          </div>

          {s.categories.length > 0 && (
            <>
              <h3>หมวดหมู่ปัญหายอดนิยม</h3>
              <table>
                <thead><tr><th>หมวดหมู่</th><th>จำนวน</th><th>เกิน SLA</th><th>เวลาแก้ไขเฉลี่ย</th></tr></thead>
                <tbody>
                  {s.categories.map((c, i) => (
                    <tr key={i}><td>{c.name}</td><td>{c.count}</td><td>{c.breached}</td><td>{hrs(c.avgResolutionHours)}</td></tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {s.topOverdue.length > 0 && (
            <>
              <h3>Ticket ค้างเกิน SLA</h3>
              <table>
                <thead><tr><th>#</th><th>หัวข้อ</th><th>Priority</th><th>สถานะ</th><th>Due Date</th><th>ผู้รับผิดชอบ</th></tr></thead>
                <tbody>
                  {s.topOverdue.map(o => (
                    <tr key={o.ticketNumber}>
                      <td>#{String(o.ticketNumber).padStart(4, '0')}</td>
                      <td>{o.title}</td>
                      <td>{o.priorityLabel}</td>
                      <td>{o.statusLabel}</td>
                      <td>{dt(o.dueDate)}</td>
                      <td>{o.assigneeName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </div>
  )
}
